import { useState, useEffect, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { LibraryItem } from '@/components/quotation/types';
import { LibraryService } from '@/lib/library-service';
import { libraryItemToQuotationItem } from '@/lib/library-utils';

interface UseLibraryProps {
  onItemAdded?: (item: any, type: string) => void;
}

export function useLibrary({ onItemAdded }: UseLibraryProps = {}) {
  const { toast } = useToast();
  const [libraryItems, setLibraryItems] = useState<LibraryItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isBrowserOpen, setIsBrowserOpen] = useState(false);
  const [selectedItemType, setSelectedItemType] = useState<string | undefined>(undefined);
  
  // Carregar itens da biblioteca
  const loadLibraryItems = useCallback(async () => {
    setIsLoading(true);
    
    try {
      const items = await LibraryService.getItems();
      setLibraryItems(items);
    } catch (error) {
      console.error('Erro ao carregar itens da biblioteca:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar os itens da biblioteca.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);
  
  // Carregar itens quando o hook é inicializado
  useEffect(() => {
    loadLibraryItems();
  }, [loadLibraryItems]);
  
  // Abrir browser da biblioteca para um tipo específico
  const openLibraryBrowser = useCallback((itemType?: string) => {
    setSelectedItemType(itemType);
    setIsBrowserOpen(true);
  }, []);
  
  // Fechar browser da biblioteca
  const closeLibraryBrowser = useCallback(() => {
    setIsBrowserOpen(false);
  }, []);
  
  // Adicionar um item da biblioteca à cotação
  const addLibraryItemToQuotation = useCallback((libraryItem: LibraryItem) => {
    try {
      // Converter o item da biblioteca para item de cotação
      const quotationItem = libraryItemToQuotationItem(libraryItem);
      
      // Chamar o callback de item adicionado, se fornecido
      if (onItemAdded) {
        onItemAdded(quotationItem, libraryItem.type);
      }
      
      // Fechar o browser
      closeLibraryBrowser();
      
      // Notificar o usuário
      toast({
        title: 'Item adicionado',
        description: `O item "${libraryItem.title}" foi adicionado com sucesso.`,
      });
      
      return quotationItem;
    } catch (error) {
      console.error('Erro ao adicionar item da biblioteca:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível adicionar o item da biblioteca.',
        variant: 'destructive',
      });
      return null;
    }
  }, [onItemAdded, closeLibraryBrowser, toast]);
  
  // Remover um item da biblioteca
  const removeLibraryItem = useCallback(async (itemId: string) => {
    try {
      const success = await LibraryService.deleteItem(itemId);
      
      if (success) {
        // Atualizar a lista de itens
        await loadLibraryItems();
        
        // Notificar o usuário
        toast({
          title: 'Item removido',
          description: 'O item foi removido da biblioteca com sucesso.',
        });
      } else {
        throw new Error('Não foi possível remover o item.');
      }
    } catch (error) {
      console.error('Erro ao remover item da biblioteca:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível remover o item da biblioteca.',
        variant: 'destructive',
      });
    }
  }, [loadLibraryItems, toast]);
  
  return {
    libraryItems,
    isLoading,
    isBrowserOpen,
    selectedItemType,
    openLibraryBrowser,
    closeLibraryBrowser,
    addLibraryItemToQuotation,
    removeLibraryItem,
    refreshLibrary: loadLibraryItems
  };
} 